import React, { useState, useEffect, useContext } from 'react';
import { Table, Container, Button, Alert } from "reactstrap";
import { UserCog, Settings, Landmark, FileText, DollarSign } from 'lucide-react';
import { AppContext } from '../application/provider';
import PagosPlanillaService from "../axios_services/pagosplanilla.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch } from '../components/Buttons/Buttons';
import ButtonUpdate from '../components/Buttons/ButtonUpdate';
import ModalColaboradorPagoChecklist from '../components/Modal/ModalColaboradorPagoChecklist';
import ModalBonosPlanilla from '../components/Modal/ModalBonosPlanilla';
import ModalAportacionesPlanilla from '../components/Modal/ModalAportacionesPlanilla';
import ModalRegistroPago from '../components/Modal/ModalRegistroPago';
import ModalValidacionPlanilla from '../components/Modal/ModalValidacionPlanilla';
import ModalEstadoPagoPlanilla from '../components/Modal/ModalEstadoPagoPlanilla';
import ArchivosService from "../axios_services/archivos.service";
import ButtonPdf from '../components/Buttons/ButtonPdf';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import "./PagosPlanilla.css";

const PagosPlanilla = () => {
    const [state] = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [configModal, setConfigModal] = useState(false);
    const [bonosModal, setBonosModal] = useState(false);
    const [aportacionesModal, setAportacionesModal] = useState(false);

    const [registroModal, setRegistroModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [validacionModal, setValidacionModal] = useState(false);
    const [estadoPagoModal, setEstadoPagoModal] = useState(false);

    const [mesFiltro, setMesFiltro] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    const allowedProfiles = [1, 5, 8, 9, 21, 22, 26];
    const userProfile = Number(state.user?.perfil_id);
    const hasAccess = allowedProfiles.includes(userProfile);

    const [areaFiltro, setAreaFiltro] = useState(userProfile === 1 ? '2' : '1');
    const [estadoFiltro, setEstadoFiltro] = useState('1');
    const [estadoAplicado, setEstadoAplicado] = useState('1');
    const isConfigAllowed = [5, 8, 22, 26].includes(userProfile);
    const canUseActions = [5, 8, 22, 26].includes(userProfile);
    const isPdfAllowed = [5, 8, 9, 21, 26].includes(userProfile);
    
    const showBtnRegistro = ![21, 12].includes(userProfile);
    const showBtnEstado = [8, 21, 12, 26].includes(userProfile);

    const loadData = async () => {
        if (!hasAccess) return;
        setLoading(true);
        try {
            const res = await PagosPlanillaService.getPagosPlanilla(mesFiltro, areaFiltro, estadoFiltro);
            const boletasData = await ArchivosService.getBoletasPagoPeriodo(mesFiltro).catch(() => []);
            const comprobantesData = await ArchivosService.getComprobantesPagoPeriodo(mesFiltro).catch(() => []);

            if (res.data.success) {
                const arrBoletas = Array.isArray(boletasData) ? boletasData : (boletasData?.data || []);
                const arrComprobantes = Array.isArray(comprobantesData) ? comprobantesData : (comprobantesData?.data || []);
                let finalData = res.data.data.map(item => {
                    const boleta = arrBoletas.find(b => b.origen_id === item.usuario_id);
                    const comprobante = arrComprobantes.find(c => c.origen_id === item.usuario_id);
                    return { ...item, boleta_url: boleta ? boleta.url_completa : null, comprobante_url: comprobante ? comprobante.url_completa : null };
                });

                if (userProfile === 21) {
                    finalData = finalData.filter(d => Number(d.validacion_id) >= 2);
                }

                setData(finalData);
                setEstadoAplicado(estadoFiltro);
            }
        } catch (error) {
            console.error("Error cargando pagos de planilla:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const formatSoles = (amount) => {
        return new Intl.NumberFormat('es-PE', {
            style: 'currency',
            currency: 'PEN',
        }).format(amount || 0);
    };

    const handleOpenRegistro = (item) => {
        setSelectedItem(item);
        setRegistroModal(true);
    };

    const handleCloseRegistro = () => {
        setRegistroModal(false);
        setSelectedItem(null);
    };

    const handleOpenEstadoPago = (item) => {
        setSelectedItem(item);
        setEstadoPagoModal(true);
    };

    const handleCloseEstadoPago = () => {
        setEstadoPagoModal(false);
        setSelectedItem(null);
    };

    const exportToPDF = async () => {
        if (data.length === 0) return;
        setLoading(true);
        try {
            const doc = new jsPDF('landscape', 'pt', 'a4');
            const getMonthName = (YYYY_MM) => {
                if (!YYYY_MM) return '';
                const [y, m] = YYYY_MM.split('-');
                const date = new Date(Number(y), Number(m) - 1, 1);
                return date.toLocaleString('es-PE', { month: 'long', year: 'numeric' }).toUpperCase();
            };
            const monthYearStr = getMonthName(mesFiltro);
            const areaName = areaFiltro === '1' ? 'ADMINISTRACIÓN' : (areaFiltro === '2' ? 'NEGOCIOS' : 'GERENCIA');

            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text("MEC BUSINNES PARTNER S.A.C.", 28, 30);
            doc.text("20612796484", 28, 45);

            const tableColumnNames = [
                "N°", "APELLIDOS Y NOMBRES", "CARGO", "REMUNERACIÓN", "BONO ANUAL", "BONO POR\nGESTIÓN",
                ...(estadoAplicado === '0' ? ["VACACIONES\nTRUNCAS"] : []),
                "DESCUENTO", "TOTAL A PAGAR\nPLANILLA", "TOTAL APORTE\nTRABAJADOR", "RETENCIÓN\n5TA CATE",
                "IMPORTE NETO\nA PAGAR", "DNI", "CUENTA", "BANCO", "FECHA DE PAGO"
            ];

            const formatNumber = (val) => {
                return new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 }).format(val || 0);
            };

            const dataToExport = data.filter(d => d.registrado);

            const tableRows = dataToExport.map((row, index) => {
                const rowData = [
                    index + 1,
                    row.nombre_completo?.toUpperCase(),
                    row.cargo?.toUpperCase(),
                    formatNumber(row.remuneracion),
                    formatNumber(row.bono_anual),
                    formatNumber(row.bono_gestion),
                ];

                if (estadoAplicado === '0') {
                    rowData.push(formatNumber(row.liquidacion));
                }

                rowData.push(
                    formatNumber(row.descuento),
                    formatNumber(row.total_pagar_planilla),
                    formatNumber(row.total_aporte_trabajador),
                    formatNumber(row.retencion_5ta),
                    formatNumber(row.importe_neto),
                    row.dni,
                    row.cuenta || '',
                    row.banco || '-',
                    row.fecha_pago || ""
                );
                return rowData;
            });

            const totalNeto = dataToExport.reduce((acc, curr) => acc + (Number(curr.importe_neto) || 0), 0);
            const footerRow = Array(tableColumnNames.length).fill(" ");
            footerRow[tableColumnNames.indexOf("IMPORTE NETO\nA PAGAR")] = formatNumber(totalNeto);

            autoTable(doc, {
                startY: 60,
                head: [
                    [{
                        content: `CARGO DE PAGO PLANILLA ${estadoAplicado === '0' ? 'CESADO ' : ''}DE ${monthYearStr} - ${areaName}`,
                        colSpan: tableColumnNames.length,
                        styles: { halign: 'center', fillColor: [42, 114, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 }
                    }],
                    tableColumnNames
                ],
                body: tableRows,
                foot: [footerRow],
                theme: 'grid',
                styles: { fontSize: 6, cellPadding: 2, halign: 'center', valign: 'middle', lineColor: [0, 0, 0], lineWidth: 0.5 },
                margin: { left: 25, right: 15 },
                headStyles: { fillColor: [255, 192, 0], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 6 },
                footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.5 },
                columnStyles: {
                    1: { halign: 'left', cellWidth: 120 },
                    2: { halign: 'left', cellWidth: 100 },
                },
                didParseCell: function (data) {
                    if (data.section === 'head' && data.row.index === 1) {
                        if (data.column.index === tableColumnNames.indexOf("DESCUENTO")) {
                            data.cell.styles.fillColor = [255, 0, 0];
                            data.cell.styles.textColor = [255, 255, 255];
                        }
                        if (data.column.index === tableColumnNames.indexOf("IMPORTE NETO\nA PAGAR")) {
                            data.cell.styles.fillColor = [254, 254, 0];
                        }
                        if (data.column.index === tableColumnNames.indexOf("FECHA DE PAGO")) {
                            data.cell.styles.fillColor = [252, 213, 180];
                        }
                    }
                    if (data.section === 'foot') {
                        const netIndex = tableColumnNames.indexOf("IMPORTE NETO\nA PAGAR");
                        if (data.column.index !== netIndex) {
                            data.cell.styles.lineWidth = 0;
                        }
                    }
                },
                didDrawCell: function (data) {
                    if (data.section === 'foot') {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(0.5);
                        const netIndex = tableColumnNames.indexOf("IMPORTE NETO\nA PAGAR");
                        if (data.column.index !== netIndex) {
                            doc.setLineWidth(0.5);
                            doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y);
                        } else {
                            doc.setLineWidth(0.5);
                            doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y); // Top
                            doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height); // Bottom
                            doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height); // Left
                            doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height); // Right
                        }
                    }
                }
            });

            const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY : 100;
            let startY = finalY + 80;

            if (startY > doc.internal.pageSize.height - 100) {
                doc.addPage();
                startY = 100;
            }

            const registrosGuardados = data.filter(d => d.registrado);
            if (registrosGuardados.length > 0) {
                const first = registrosGuardados[0];

                /*
                // =========================================================================
                // CODIGO ANTERIOR (3 FIRMAS FIJAS CON TALENTO HUMANO / PERFIL 5):
                // Descomentar cuando el perfil 5 vuelva a contar con jefatura independiente:
                // =========================================================================
                // const [firmaThRes, firmaGlRes, firmaGgRes] = await Promise.all([
                //     first.aprobador_th_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_th_id).catch(() => null) : null,
                //     first.aprobador_gl_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_gl_id).catch(() => null) : null,
                //     first.aprobador_gg_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_gg_id).catch(() => null) : null
                // ]);
                // const pageWidth = doc.internal.pageSize.width;
                // const centers = [pageWidth * 0.20, pageWidth * 0.50, pageWidth * 0.80];
                // const glRole = areaFiltro === '2' ? 'GERENTE DE NEGOCIOS' : 'SUBGERENTE DE ADMINISTRACIÓN';
                // if (first.aprobador_th_id) drawSignature(firmaThRes?.base64, first.nombre_th, "JEFE DE TALENTO HUMANO", centers[0], startY);
                // if (first.aprobador_gl_id) drawSignature(firmaGlRes?.base64, first.nombre_gl, glRole, centers[1], startY);
                // if (first.aprobador_gg_id) drawSignature(firmaGgRes?.base64, first.nombre_gg, "GERENCIA GENERAL", centers[2], startY);
                // =========================================================================
                */

                const esGerenciaDistinta = areaFiltro === '2'; // Área de Negocios
                const pageWidth = doc.internal.pageSize.width;

                const drawSignature = (b64, name, role, cx, y) => {
                    if (b64) {
                        const format = b64.includes('png') ? 'PNG' : 'JPEG';
                        doc.addImage(b64, format, cx - 40, y - 45, 80, 40);
                    }
                    doc.setLineWidth(0.5);
                    doc.line(cx - 70, y, cx + 70, y);
                    doc.setFontSize(7);
                    doc.setFont("helvetica", "bold");
                    doc.text(role, cx, y + 12, { align: 'center' });
                    doc.setFont("helvetica", "normal");
                    doc.text(name || '', cx, y + 22, { align: 'center' });
                };

                const isHistorical = mesFiltro < '2026-09';

                if (isHistorical) {
                    // HISTÓRICO (Agosto 2026 y anteriores): Preserva la representación de 3 firmas si existen
                    const [firmaThRes, firmaGlRes, firmaGgRes] = await Promise.all([
                        first.aprobador_th_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_th_id).catch(() => null) : null,
                        first.aprobador_gl_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_gl_id).catch(() => null) : null,
                        first.aprobador_gg_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_gg_id).catch(() => null) : null
                    ]);
                    const centers = [pageWidth * 0.20, pageWidth * 0.50, pageWidth * 0.80];
                    const thRole = "JEFE DE TALENTO HUMANO";
                    const glRole = areaFiltro === '2' ? "GERENTE DE NEGOCIOS" : "SUBGERENTE DE ADMINISTRACIÓN";
                    if (first.aprobador_th_id) drawSignature(firmaThRes?.base64, first.nombre_th, thRole, centers[0], startY);
                    if (first.aprobador_gl_id) drawSignature(firmaGlRes?.base64, first.nombre_gl, glRole, centers[1], startY);
                    if (first.aprobador_gg_id) drawSignature(firmaGgRes?.base64, first.nombre_gg, "GERENCIA GENERAL", centers[2], startY);
                } else if (esGerenciaDistinta) {
                    // SEPTIEMBRE 2026 EN ADELANTE - Área de Negocios (Comercial): 3 firmas
                    const [firmaThRes, firmaGlRes, firmaGgRes] = await Promise.all([
                        first.aprobador_th_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_th_id).catch(() => null) : null,
                        first.aprobador_gl_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_gl_id).catch(() => null) : null,
                        first.aprobador_gg_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_gg_id).catch(() => null) : null
                    ]);
                    const centers = [pageWidth * 0.20, pageWidth * 0.50, pageWidth * 0.80];
                    if (first.aprobador_th_id) drawSignature(firmaThRes?.base64, first.nombre_th, "SUB GERENCIA DE ADMINISTRACIÓN", centers[0], startY);
                    if (first.aprobador_gl_id) drawSignature(firmaGlRes?.base64, first.nombre_gl, "GERENTE DE NEGOCIOS", centers[1], startY);
                    if (first.aprobador_gg_id) drawSignature(firmaGgRes?.base64, first.nombre_gg, "GERENCIA GENERAL", centers[2], startY);
                } else {
                    // SEPTIEMBRE 2026 EN ADELANTE - Área de Administración: 1 sola firma con GERENCIA GENERAL
                    const [firmaGgRes] = await Promise.all([
                        first.aprobador_gg_id ? ArchivosService.getFirmaDigitalConvenio(first.aprobador_gg_id).catch(() => null) : null
                    ]);
                    const center = pageWidth * 0.50;
                    if (first.aprobador_gg_id) drawSignature(firmaGgRes?.base64, first.nombre_gg, "GERENCIA GENERAL", center, startY);
                }
            }

            doc.save(`Gestion_Planilla_${areaName}_${mesFiltro}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Error al generar el PDF");
        } finally {
            setLoading(false);
        }
    };

    if (!hasAccess) {
        return (
            <Container className="mt-5 text-center">
                <h3>Acceso Restringido</h3>
                <p>No tiene permisos para ver este módulo.</p>
            </Container>
        );
    }

    return (
        <div className="cargo-pago-module">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>GESTIÓN DE PLANILLA</h1>
                <div className="flex gap-2">
                    {isConfigAllowed && (
                        <>
                            <Button
                                color="success"
                                outline
                                style={{ borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setAportacionesModal(true)}
                                title="Configurar Aportaciones"
                            >
                                <Landmark size={28} />
                            </Button>
                            <Button
                                color="info"
                                outline
                                style={{ borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setBonosModal(true)}
                                title="Configurar Bonos"
                            >
                                <Settings size={28} />
                            </Button>
                            <Button
                                color="primary"
                                outline
                                style={{ borderRadius: '8px', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                onClick={() => setConfigModal(true)}
                                title="Configurar Colaboradores"
                            >
                                <UserCog size={28} />
                            </Button>
                        </>
                    )}
                    {isPdfAllowed && (
                        <ButtonPdf
                            onClick={exportToPDF}
                            disabled={loading || data.length === 0 || !data.some(d => d.registrado)}
                        />
                    )}
                </div>
            </div>

            <div className="filtros">
                <div>
                    <label htmlFor="mesFiltro">Periodo de fecha:</label>
                    <input
                        id="mesFiltro"
                        type="month"
                        value={mesFiltro}
                        onChange={(e) => setMesFiltro(e.target.value)}
                    />
                </div>
                <div>
                    <label htmlFor="areaFiltro">Área:</label>
                    <select
                        id="areaFiltro"
                        className="form-control"
                        value={areaFiltro}
                        onChange={(e) => setAreaFiltro(e.target.value)}
                        disabled={userProfile === 1}
                    >
                        {userProfile !== 1 && <option value="1">ADMINISTRACIÓN</option>}
                        <option value="2">NEGOCIOS</option>
                        {userProfile !== 1 && <option value="4">GERENCIA</option>}
                    </select>
                </div>
                <div>
                    <label htmlFor="estadoFiltro">Estado:</label>
                    <select
                        id="estadoFiltro"
                        className="form-control"
                        value={estadoFiltro}
                        onChange={(e) => setEstadoFiltro(e.target.value)}
                    >
                        <option value="1">ACTIVOS</option>
                        <option value="0">CESADOS</option>
                    </select>
                </div>
            </div>

            <div className="flex justify-start items-center gap-3 mb-4">
                <ButtonSearch onClick={loadData} isLoading={loading} />
            </div>

            {(() => {
                const registros = data.filter(d => d.registrado);
                if (registros.length === 0) return null;
                const currentValId = registros[0].validacion_id;
                let estadoNom = 'PENDIENTE';
                if (currentValId === 2) estadoNom = 'VALIDADO POR TALENTO HUMANO';
                if (currentValId === 3) estadoNom = 'VALIDADO POR GERENCIA DE NEGOCIOS';
                if (currentValId === 4) estadoNom = 'VALIDADO POR SUBGERENCIA ADMINISTRATIVA';
                if (currentValId === 5) estadoNom = 'VALIDADO POR GERENCIA GENERAL';
                if (currentValId === 6) estadoNom = 'OBSERVADO / RECHAZADO';

                let canShowApprove = false;
                if (userProfile === 8) {
                    canShowApprove = true;
                } else if (userProfile === 26 && (currentValId === 0 || currentValId === 6)) {
                    canShowApprove = true;
                } else if (userProfile === 1 && areaFiltro === '2' && currentValId === 2) {
                    canShowApprove = true;
                } else if (userProfile === 9) {
                    if (['1', '4'].includes(areaFiltro) && (currentValId === 2 || currentValId === 4)) canShowApprove = true;
                    if (areaFiltro === '2' && currentValId === 3) canShowApprove = true;
                }
                if (currentValId === 5) canShowApprove = false;

                return (
                    <div className="flex justify-between items-center mb-6">
                        <div style={{
                            backgroundColor: '#f5f5f5',
                            padding: '10px 20px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1px solid #e0e0e0'
                        }}>
                            <span className="text-sm text-gray-700 uppercase tracking-wider" style={{ fontWeight: '400' }}>
                                {estadoNom}
                            </span>
                        </div>

                        {canShowApprove && (
                            <Button
                                className="bg-green-600 hover:bg-green-700 text-white border-none shadow-sm"
                                style={{
                                    borderRadius: '8px',
                                    padding: '8px 25px',
                                    fontWeight: 'semibold',
                                    fontSize: '13px'
                                }}
                                onClick={() => setValidacionModal(true)}
                            >
                                Aprobar
                            </Button>
                        )}
                    </div>
                );
            })()}

            <div className="table-container shadow-sm overflow-x-auto">
                <Table bordered hover size="sm" className="text-xs m-0" style={{ tableLayout: 'fixed', width: '100%', minWidth: '1600px' }}>
                    <thead className="bg-[#14233a] text-white text-center align-middle">
                        <tr className="leading-tight">
                            <th className="p-1" style={{ width: '40px' }}>N°</th>
                            <th className="p-1" style={{ width: '250px', whiteSpace: 'normal' }}>APELLIDOS Y NOMBRE</th>
                            <th className="p-1" style={{ width: '250px', whiteSpace: 'normal' }}>CARGO</th>
                            <th className="p-1" style={{ width: '140px' }}>RENUMERACIÓN</th>
                            <th className="p-1" style={{ width: '140px', whiteSpace: 'normal' }}>BONO<br />ANUAL</th>
                            <th className="p-1" style={{ width: '140px', whiteSpace: 'normal' }}>BONO POR<br />GESTIÓN</th>
                            {estadoAplicado === '0' && <th className="p-1" style={{ width: '160px', whiteSpace: 'normal' }}>VACACIONES<br />TRUNCAS</th>}
                            <th className="p-1" style={{ width: '140px' }}>DESCUENTO</th>
                            <th className="p-1" style={{ width: '140px', whiteSpace: 'normal' }}>TOTAL A PAGAR<br />PLANILLA</th>
                            <th className="p-1" style={{ width: '140px', whiteSpace: 'normal' }}>TOTAL APORTE<br />TRABAJADOR</th>
                            <th className="p-1" style={{ width: '140px', whiteSpace: 'normal' }}>RETENCIÓN<br />5TA CATEGORÍA</th>
                            <th className="p-1" style={{ width: '140px', whiteSpace: 'normal' }}>IMPORTE NETO<br />A PAGAR</th>
                            <th className="p-1" style={{ width: '80px' }}>DNI</th>
                            <th className="p-1" style={{ width: '130px' }}>CUENTA</th>
                            <th className="p-1" style={{ width: '100px' }}>BANCO</th>
                            <th className="p-1" style={{ width: '100px' }}>BOLETA DE<br />PAGO</th>
                            <th className="p-1" style={{ width: '90px', whiteSpace: 'normal' }}>ESTADO<br />PAGO</th>
                            <th className="p-1" style={{ width: '100px', whiteSpace: 'normal' }}>FECHA DE<br />PAGO</th>
                            <th className="p-1" style={{ width: '130px', whiteSpace: 'normal' }}>COMPROBANTE<br />DE PAGO</th>
                            <th className="p-1" style={{ width: '100px' }}>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody className="align-middle">
                        {data.length > 0 ? (
                            data.map((item, index) => {
                                const getRowClass = (it) => {
                                    if (!it.registrado) return '';
                                    if (it.validacion_id === 6) return 'row-rechazado';
                                    if (it.validacion_id >= 2) return 'row-validado';
                                    return 'row-registrado';
                                };
                                const isLocked = (item.validacion_id >= 2 && item.validacion_id !== 6) || !canUseActions;

                                return (
                                    <tr key={index} className={getRowClass(item)}>
                                        <td className="p-1 text-center">{index + 1}</td>
                                        <td className="p-1 font-bold text-uppercase">
                                            {item.nombre_completo}
                                        </td>
                                        <td className="p-1">{item.cargo}</td>
                                        <td className="p-1 text-center font-semibold" style={{ width: '130px' }}>
                                            {item.remuneracion ? (
                                                formatSoles(item.remuneracion)
                                            ) : (
                                                <span className="text-red-500 font-bold" style={{ fontSize: '9px' }}>
                                                    ⚠️ FALTA ASIGNAR SUELDO
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-1 text-center">
                                            {item.aplica_bono_anual === 1 ? formatSoles(item.bono_anual) : 'S/ 0.00'}
                                        </td>
                                        <td className="p-1 text-center">
                                            {item.aplica_bono_gestion === 1 ? formatSoles(item.bono_gestion) : 'S/ 0.00'}
                                        </td>
                                        {estadoAplicado === '0' && (
                                            <td className="p-1 text-center font-bold text-success">
                                                {formatSoles(item.liquidacion)}
                                            </td>
                                        )}
                                        <td className="p-1 text-center text-red-600 font-semibold">{formatSoles(item.descuento)}</td>
                                        <td className="p-1 text-center font-bold text-blue-700">{formatSoles(item.total_pagar_planilla)}</td>
                                        <td className="p-1 text-center text-red-700">
                                            {item.tipo_aportacion ? (
                                                formatSoles(item.total_aporte_trabajador)
                                            ) : (
                                                <span className="text-red-500 font-bold" style={{ fontSize: '9px' }}>
                                                    ⚠️ FALTA ASIGNAR APORTACIÓN
                                                </span>
                                            )}
                                        </td>
                                        <td className="p-1 text-center">
                                            {formatSoles(item.retencion_5ta)}
                                        </td>
                                        <td className="p-1 text-center font-bold">{formatSoles(item.importe_neto)}</td>
                                        <td className="p-1 text-center">{item.dni}</td>
                                        <td className="p-1 text-center">{item.cuenta || '-'}</td>
                                        <td className="p-1 text-center">{item.banco || '-'}</td>
                                        <td className="p-1 text-center">
                                            <div className="flex justify-center items-center w-full">
                                                {item.boleta_url ? (
                                                    <a href={item.boleta_url} target="_blank" rel="noreferrer" title="Ver Boleta de Pago">
                                                        <FileText size={18} style={{ color: '#3cb167', cursor: 'pointer' }} />
                                                    </a>
                                                ) : (
                                                    <FileText size={18} style={{ color: '#f87171', opacity: 0.5 }} title="Sin boleta disponible" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-1 text-center">
                                            {item.registrado ? (
                                                <span className={`badge ${Number(item.pago_id) === 2 ? 'bg-success' : (Number(item.pago_id) === 1 ? 'bg-secondary' : 'bg-warning')}`} style={{ fontSize: '9px' }}>
                                                    {state.catalogos.tipos.find(t => t.categoria_id == 32 && t.tipo_id == item.pago_id)?.descripcion || item.estado_pago}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td className="p-1 text-center">{item.fecha_pago || '-'}</td>
                                        <td className="p-1 text-center">
                                            <div className="flex justify-center items-center w-full">
                                                {item.comprobante_url ? (
                                                    <a href={item.comprobante_url} target="_blank" rel="noreferrer" title="Ver Comprobante de Pago">
                                                        <FileText size={18} style={{ color: '#3cb167', cursor: 'pointer' }} />
                                                    </a>
                                                ) : (
                                                    <FileText size={18} style={{ color: '#f87171', opacity: 0.5 }} title="Sin comprobante disponible" />
                                                )}
                                            </div>
                                        </td>
                                        <td className="p-1 text-center align-middle">
                                            <div className="flex justify-center items-center w-full gap-2">
                                                {showBtnRegistro && (
                                                    <ButtonUpdate
                                                        onClick={() => handleOpenRegistro(item)}
                                                        title={!canUseActions ? "Ver Registro" : "Actualizar Registro"}
                                                    />
                                                )}
                                                {item.validacion_id >= 2 && item.validacion_id !== 6 && showBtnEstado && (
                                                    <ButtonUpdate
                                                        onClick={() => handleOpenEstadoPago(item)}
                                                        title="Actualizar Estado de Pago"
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="19" className="text-center py-4 text-muted">
                                    No se encontraron registros para el periodo y área seleccionados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalColaboradorPagoChecklist
                isOpen={configModal}
                onClose={() => setConfigModal(false)}
                onRefresh={loadData}
            />

            <ModalBonosPlanilla
                isOpen={bonosModal}
                onClose={() => setBonosModal(false)}
                onRefresh={loadData}
            />

            <ModalAportacionesPlanilla
                isOpen={aportacionesModal}
                onClose={() => setAportacionesModal(false)}
                onRefresh={loadData}
            />

            <ModalRegistroPago
                isOpen={registroModal}
                onClose={handleCloseRegistro}
                onRefresh={loadData}
                item={selectedItem}
                periodo={mesFiltro}
            />

            <ModalValidacionPlanilla
                isOpen={validacionModal}
                onClose={() => setValidacionModal(false)}
                onRefresh={loadData}
                periodo={mesFiltro}
                areaId={areaFiltro}
                data={data}
                situacion={estadoAplicado}
            />

            <ModalEstadoPagoPlanilla
                isOpen={estadoPagoModal}
                onClose={handleCloseEstadoPago}
                onRefresh={loadData}
                item={selectedItem}
                periodo={mesFiltro}
            />
        </div>
    );
};

export default PagosPlanilla;